import { DateTime, Duration } from 'luxon';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import useQueryParams from '@/hooks/useQueryParams';
import {
  INSIGHTS_DATE_FROM_KEY,
  INSIGHTS_DATE_TO_KEY,
  INSIGHTS_DURATION_KEY,
  INSIGHTS_INTERVAL_KEY,
  TIME_INTERVAL_DEFAULT,
  TIME_RANGE_DEFAULT,
} from '@/pages/Insights/Insights.constants';
import { TypeDuration, TypeTimeInterval } from '@/pages/Insights/Insights.types';
import { mergeQueryParams } from '@/utils/queryParams';

import { TimeWindowMode } from '../components/CycleInfo/CycleInfo.types';

type UrlContextProviderProps = { children: ReactNode };

export type UrlContextType = {
  interval: TypeTimeInterval;
  duration: TypeDuration;
  dateFromAsUnixSeconds?: number;
  dateToAsUnixSeconds?: number;
  localDateFrom?: Date;
  localDateTo?: Date;
  selectedCycleString?: string;
  timeWindowMode: TimeWindowMode;
  utcDateFrom?: Date;
  utcDateTo?: Date;
};

const UrlContext = createContext<UrlContextType>({
  interval: TIME_INTERVAL_DEFAULT,
  duration: TIME_RANGE_DEFAULT,
  dateFromAsUnixSeconds: undefined,
  dateToAsUnixSeconds: undefined,
  selectedCycleString: undefined,
  timeWindowMode: 'billing_cycle',
});

const getFromStorage = (key: string) => {
  let queryKey;
  switch (key) {
    case INSIGHTS_INTERVAL_KEY:
      queryKey = 'interval';
      break;

    case INSIGHTS_DURATION_KEY:
      queryKey = 'duration';
      break;

    case INSIGHTS_DATE_FROM_KEY:
      queryKey = 'dateFrom';
      break;

    case INSIGHTS_DATE_TO_KEY:
      queryKey = 'dateTo';
      break;

    default:
      queryKey = '';
      break;
  }
  const value = localStorage.getItem(key);
  const path = window.location.pathname;

  if (queryKey && value && (path.includes('overview') || path.includes('explore'))) {
    mergeQueryParams(
      {
        set: [{ type: queryKey, value }],
      },
      'replace',
    );
  }
  return value;
};

const calcFiltersFromUrl = (params: URLSearchParams): UrlContextType => {
  const dateFromString = params.get('dateFrom') || getFromStorage(INSIGHTS_DATE_FROM_KEY);
  const dateFromAsUnixSeconds = dateFromString ? parseInt(dateFromString, 10) : undefined;
  const dateToString = params.get('dateTo') || getFromStorage(INSIGHTS_DATE_TO_KEY);
  const dateToAsUnixSeconds = dateToString ? parseInt(dateToString, 10) : undefined;
  const selectedCycleString = params.get('selectedCycleString') || undefined;
  const timeWindowMode = (params.get('timeWindowMode') as TimeWindowMode) || 'billing_cycle';

  let interval =
    (params.get('interval') as TypeTimeInterval) || getFromStorage(INSIGHTS_INTERVAL_KEY) || TIME_INTERVAL_DEFAULT;
  let duration =
    (params.get('duration') as TypeDuration) || getFromStorage(INSIGHTS_DURATION_KEY) || TIME_RANGE_DEFAULT;

  if (duration === 'FIXED' && (!dateFromAsUnixSeconds || !dateToAsUnixSeconds)) {
    interval = TIME_INTERVAL_DEFAULT;
    duration = TIME_RANGE_DEFAULT;
  }

  return {
    interval,
    duration,
    dateFromAsUnixSeconds,
    dateToAsUnixSeconds,
    selectedCycleString,
    timeWindowMode,
  };
};

export const UrlContextProvider = ({ children }: UrlContextProviderProps) => {
  const queryParams = useQueryParams();

  const value = useMemo(() => calcFiltersFromUrl(queryParams), [queryParams]);

  return <UrlContext.Provider value={value}>{children}</UrlContext.Provider>;
};

const resolutionMap: Record<TypeTimeInterval, 'second' | 'hour' | 'day' | 'week' | 'month' | 'year'> = {
  secondly: 'second',
  hourly: 'hour',
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
};

export const bucketStartOfInterval = (interval: TypeTimeInterval, start: DateTime): DateTime => {
  return start.startOf(resolutionMap[interval]);
};

export const bucketEndOfInterval = (interval: TypeTimeInterval, end: DateTime): DateTime => {
  return end.endOf(resolutionMap[interval]);
};

export const useUrl = () => {
  const value = useContext(UrlContext);
  const { interval, duration, dateFromAsUnixSeconds, dateToAsUnixSeconds, timeWindowMode } = value;

  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  const endDate =
    duration === 'FIXED' ? DateTime.fromSeconds(dateToAsUnixSeconds!).toUTC() : DateTime.now().startOf('hour').toUTC();

  const startDate =
    duration === 'FIXED'
      ? DateTime.fromSeconds(dateFromAsUnixSeconds!).toUTC()
      : endDate.minus(Duration.fromISO(duration as string));

  /* eslint-enable @typescript-eslint/no-non-null-assertion */
  const start = bucketStartOfInterval(interval, startDate).toMillis();
  const end = bucketEndOfInterval(interval, endDate).toMillis();
  const chartStart = start;
  const chartEnd = duration === 'FIXED' ? bucketStartOfInterval(interval, endDate).toMillis() : endDate.toMillis();

  return {
    ...value,
    start,
    end,
    chartStart,
    chartEnd,
    timeWindowMode,
    dateFromAsUnixSeconds,
    dateToAsUnixSeconds,
    duration,
    interval,
  };
};
